/**
 * v4.32 - 2026-10-20 - AIRBYTE-CONNECTOR-ROLLOUT-01 (F1, F3): the sign-in type comes from the server's projected
 *                      authType with no default; oauth2 opens the provider window, api_key / basic the form, anything
 *                      else (absent, unknown, or a form type whose form is unavailable) an error and no window. The
 *                      popup error plan_limit_reached shows the plan-limit sentence.
 * v4.28 - 2026-10-13 - G10-B (UI-PERMISSION-LIST-NOT-ENFORCED-01): connect, sync, disconnect, delegate and document upload are shown only when the
 *                      server's permission map for the chosen company says yes (manageConnections, delegateConnections, uploadDocuments)
 * DataSources - Manage connected data sources
 * v4.22 - 2026-10-02 - CONNECTOR-HEALTH-STATE-H3 (H3-9; F85): per-type health dot and its 60 s poll removed; Report
 *                      Issue shows for a connection whose own healthState is needs_attention
 * v4.21 - 2026-10-01 - CONNECTOR-HEALTH-STATE-H2 (F16, F17, F18, F84, F89): chip, Issue Reported, refresh and the
 *                      Connect dialog render from the server verdict (utils/connectionHealthDisplay); SyncStatusChip
 *                      removed; list polled while a connection is in progress; first-load error state with Retry;
 *                      manual-sync failure / timeout messages
 * v4.08 - 2026-04-26 - UNIFY-01: post-setup connector add triggers gold-only refresh
 * v4.01 - 2026-04-20 - UNSTRUCTURED-01 Phase 3: ReconciliationQueue section below DocumentUploadSection
 * v3.99 - 2026-04-19 - UNSTRUCTURED-01: DocumentUploadSection injected below connector cards
 * v3.97 - 2026-04-07 - EXCEL-04: Excel/CSV card injected at top of Connect New Source dialog
 * v3.96 - 2026-04-05 - EXCEL-01 P5: Upload a File dashed card + ExcelUpload dialog
 * v3.95 - 2026-04-02 - Handle flat {connectors:[]} response shape (Azure Front Door direct routing)
 * v3.00 - 2026-02-07 - Connector health status dots, Report Issue button, 60s health polling
 * v2.81 - 2026-02-05 - Fix popup OAuth not refreshing connector list on return
 * v2.80 - 2026-02-05 - OAuth opens in popup window (keeps browser history clean)
 * v2.26 - 2026-01-30 - Complete rewrite with dynamic connectors, forms, and delegation
 *
 * Features:
 * - Shows all connected data sources with status
 * - Add new connections (OAuth, API Key, Basic Auth)
 * - Disconnect existing sources
 * - Delegate connection to accountant/bookkeeper
 * - Sync status and last sync time
 */
import React, { useEffect, useState, useCallback, useRef } from 'react'; // v4.01 - added useRef
import DocumentUploadSection from '../components/DocumentUploadSection'; // v3.99 - UNSTRUCTURED-01
import { usePermissions } from '../hooks/usePermissions'; // v4.28 - G10-B: screens follow the server's permission map
import {
  Box, Container, Typography, Grid, Card, CardContent, Button, Chip,
  Skeleton, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Menu, MenuItem, Divider, Tooltip, LinearProgress,
} from '@mui/material';
import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  CloudDone, CloudOff, Sync, SyncProblem, Add, MoreVert,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Refresh, Delete, PersonAdd, Link as LinkIcon, CheckCircle,
  Error as ErrorIcon, Schedule, Storage,
  HelpOutline, Gavel,  // v4.01 - reconciliation queue icons
} from '@mui/icons-material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PaymentIcon from '@mui/icons-material/Payment';
import PeopleIcon from '@mui/icons-material/People';
import CampaignIcon from '@mui/icons-material/Campaign';
import CloudIcon from '@mui/icons-material/Cloud';
import BusinessIcon from '@mui/icons-material/Business';
import TopBar from '../components/TopBar';
import CredentialFormModal from '../components/CredentialFormModal';
import DelegationModal from '../components/DelegationModal';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
// v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-4): SyncStatusChip (v3.66 AIR-04) removed; the verdict is healthState.
import {
  healthDisplay, normalizeHealthState, shouldPollList, isRefreshDisabled, dialogActionFor, manualSyncFailure,
  LIST_POLL_INTERVAL_MS, HealthState,
} from '../utils/connectionHealthDisplay';
import { TextField, InputAdornment } from '@mui/material'; // v3.95 - Pre-auth modal
import api, {
  connectionsService, connectorsService, authService, connectorHealthService,
  dashboardKPIService,
  Connection, ConnectorInfo, PreAuthParam, FormSchema
} from '../services/api';

// Icon mapping from Cosmos DB icon names to MUI components
const iconMap: Record<string, React.ReactNode> = {
  'Storefront': <StorefrontIcon />,
  'AccountBalance': <AccountBalanceIcon />,
  'PointOfSale': <PointOfSaleIcon />,
  'Payment': <PaymentIcon />,
  'People': <PeopleIcon />,
  'Campaign': <CampaignIcon />,
  'Cloud': <CloudIcon />,
};

// v4.32 - 2026-10-20 - AIRBYTE-CONNECTOR-ROLLOUT-01 (F1, F3): customer sentences for the two refusals below.
const FORM_UNAVAILABLE_MESSAGE = 'This connection form is not available right now. Please contact support.';
const PLAN_LIMIT_ERROR = 'plan_limit_reached';
const PLAN_LIMIT_MESSAGE = "Your plan's connection limit is reached. Upgrade your plan to add more connections.";

const getIcon = (iconName: string, size: number = 32): React.ReactNode => {
  const IconComponent = iconMap[iconName];
  if (IconComponent) {
    return React.cloneElement(IconComponent as React.ReactElement, { sx: { fontSize: size } });
  }
  return <BusinessIcon sx={{ fontSize: size }} />;
};

// Category labels
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const categoryLabels: Record<string, string> = {
  'accounting': 'Accounting',
  'ecommerce': 'E-Commerce',
  'payments': 'Payments',
  'hr': 'HR & Payroll',
  'crm': 'CRM',
  'marketing': 'Marketing',
  'other': 'Other',
};

const DataSources: React.FC = () => {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [connectors, setConnectors] = useState<{
    matched: ConnectorInfo[];
    recommended: ConnectorInfo[];
    other: Record<string, ConnectorInfo[]>;
  }>({ matched: [], recommended: [], other: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-7b, PD-4): has the connections list loaded at least once?
  const [loadState, setLoadState] = useState<'loading' | 'loaded' | 'error'>('loading');
  // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-7c): info notice when a manual sync outlives the browser timeout
  const [syncNotice, setSyncNotice] = useState<string | null>(null);
  // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-3c, PD-2): connector being reconnected through the Disconnect dialog
  const [reconnectConnector, setReconnectConnector] = useState<ConnectorInfo | null>(null);
  // v4.28 - G10-B (UI-PERMISSION-LIST-NOT-ENFORCED-01): what the server says this person may do in the chosen company
  const { hasPermission } = usePermissions();
  const canManageConnections = hasPermission('manageConnections');
  const canDelegateConnections = hasPermission('delegateConnections');
  const canUploadDocuments = hasPermission('uploadDocuments');
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuConnection, setMenuConnection] = useState<Connection | null>(null);

  // Credential form state
  const [credentialModalOpen, setCredentialModalOpen] = useState(false);
  const [credentialFormSchema, setCredentialFormSchema] = useState<FormSchema | null>(null);
  const [credentialConnector, setCredentialConnector] = useState<ConnectorInfo | null>(null);
  const [credentialLoading, setCredentialLoading] = useState(false);
  const [credentialError, setCredentialError] = useState<string | null>(null);

  // Delegation modal state
  const [delegationModalOpen, setDelegationModalOpen] = useState(false);
  const [delegationConnector, setDelegationConnector] = useState<ConnectorInfo | null>(null);

  // v3.95 - Pre-auth modal state (Shopify store picker, etc.) - 2026-04-06
  const [preAuthModalOpen, setPreAuthModalOpen] = useState(false);
  const [preAuthConnector, setPreAuthConnector] = useState<ConnectorInfo | null>(null);
  const [preAuthValues, setPreAuthValues] = useState<Record<string, string>>({});
  const [preAuthErrors, setPreAuthErrors] = useState<Record<string, string>>({});

  // v2.41 - Sync state
  const [syncingConnectionId, setSyncingConnectionId] = useState<string | null>(null);

  // v2.42 - Company name from tenant_meta (set once during discovery)
  const [companyName, setCompanyName] = useState<string | null>(null);

  // v3.00 - Report Issue in flight (v4.22 - H3: the per-type health state is retired)
  const [reportingIssue, setReportingIssue] = useState<string | null>(null);

  // v4.01 - UNSTRUCTURED-01 Phase 3: reconciliation queue state
  const [recoQueue, setRecoQueue] = useState<any[]>([]);
  const [recoTotal, setRecoTotal] = useState(0);
  const [recoLoading, setRecoLoading] = useState(false);
  const [recoDeciding, setRecoDeciding] = useState<string | null>(null);
  const [recoError, setRecoError] = useState<string | null>(null);
  const recoFetchedRef = useRef(false);

  // v4.08 - UNIFY-01: banner shown after a connector is successfully added
  const [refreshBanner, setRefreshBanner] = useState(false);

  const tenantId = sessionStorage.getItem('userTenantId');

  // v4.08 - UNIFY-01: fire-and-forget gold refresh after a new connector is added
  const triggerGoldRefresh = useCallback(() => {
    if (!tenantId) return;
    setRefreshBanner(true);
    setTimeout(() => setRefreshBanner(false), 8000);
    dashboardKPIService.dashboardRefresh(tenantId).catch(() => {/* non-blocking */});
  }, [tenantId]);

  // Fetch connections and connectors
  const fetchData = useCallback(async () => {
    if (!tenantId) {
      setError('Not authenticated. Please refresh the page.');
      setLoadState('error'); // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-7b): not an empty account
      setLoading(false);
      return;
    }

    setLoading(true);
    // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-7b, PD-4): the two requests settle independently. A failed
    // connections list before any successful load renders the error state (never the empty-account panel);
    // a catalog-only failure keeps the list and shows the alert.
    const [connectionsResult, connectorsResult] = await Promise.allSettled([
      connectionsService.list(tenantId),
      connectorsService.getForTenant(),
    ]);
    if (connectionsResult.status === 'fulfilled') {
      setConnections(connectionsResult.value.connections || []);
      setLoadState('loaded');
    } else {
      console.error('Failed to fetch connections:', connectionsResult.reason);
      setLoadState(prev => (prev === 'loaded' ? 'loaded' : 'error'));
    }
    try {
      if (connectorsResult.status === 'rejected') throw connectorsResult.reason;
      const connectorsData = connectorsResult.value as any;
      // v3.95 - Handle both BFF grouped {matched, recommended, other} and direct flat {connectors:[]} responses
      // (Azure Front Door can bypass BFF and route directly to d75-agent /api/connectors)
      if (connectorsData.matched !== undefined || connectorsData.recommended !== undefined || connectorsData.other !== undefined) {
        setConnectors({
          matched: connectorsData.matched || [],
          recommended: connectorsData.recommended || [],
          other: connectorsData.other || {},
        });
      } else if (Array.isArray(connectorsData.connectors)) {
        const rec: ConnectorInfo[] = [];
        const grouped: Record<string, ConnectorInfo[]> = {};
        for (const conn of connectorsData.connectors) {
          if (conn.popularForSmb) {
            rec.push(conn);
          } else {
            const cat = conn.category || 'Other';
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push(conn);
          }
        }
        setConnectors({ matched: [], recommended: rec, other: grouped });
      }
      if (connectionsResult.status === 'rejected') throw connectionsResult.reason;
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch data:', err);
      setError(err?.userMessage || 'Failed to load data sources');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-7a, PD-3): quiet list refresh — no skeleton, no catalog refetch, an
  // in-flight guard against overlapping requests; a failed refresh keeps the last list.
  const refreshInFlight = useRef(false);
  const refreshConnections = useCallback(async () => {
    if (!tenantId || refreshInFlight.current) return;
    refreshInFlight.current = true;
    try {
      const data = await connectionsService.list(tenantId);
      setConnections(data.connections || []);
      setLoadState('loaded');
    } catch (err) {
      console.debug('v4.21: connection list refresh failed (last list kept):', err);
    } finally {
      refreshInFlight.current = false;
    }
  }, [tenantId]);

  // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-7a): while any listed connection is syncing or setting up, re-fetch the
  // list every 15 s and on window focus; stop when none is.
  const listInProgress = shouldPollList(connections);
  useEffect(() => {
    if (!listInProgress) return undefined;
    const interval = setInterval(() => { refreshConnections(); }, LIST_POLL_INTERVAL_MS);
    const onFocus = () => { refreshConnections(); };
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [listInProgress, refreshConnections]);

  useEffect(() => {
    fetchData();
    // v2.42 - Get company name from session storage
    const storedCompanyName = sessionStorage.getItem('userCompanyName');
    if (storedCompanyName) {
      setCompanyName(storedCompanyName);
    }
  }, [fetchData]);

  // v4.25 - SCREENS-301: reconciliation queue fetch and decide routed via the authenticated
  //         axios client (api). The previous raw-fetch pattern read a `clerkToken` from
  //         sessionStorage that nothing wrote, so Authorization was always `Bearer ` and
  //         validate_tenant 401'd. The axios interceptor attaches X-Authorization from
  //         Clerk. - 2026-10-05
  const fetchRecoQueue = useCallback(async () => {
    if (recoFetchedRef.current) return;
    recoFetchedRef.current = true;
    setRecoLoading(true);
    try {
      const res = await api.get<{ items?: any[]; total?: number }>(
        '/ingest/document/reconciliation-queue',
        { params: { limit: 50, offset: 0 } }
      );
      const data = res.data;
      setRecoQueue(data.items || []);
      setRecoTotal(data.total || 0);
    } catch (err: any) {
      console.debug('v4.25: Reconciliation queue fetch failed (non-critical):', err);
      // Non-critical — section simply won't render if 0 items
    } finally {
      setRecoLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecoQueue(); }, [fetchRecoQueue]);

  const handleRecoDecide = async (decisionId: string, decision: 'same' | 'different') => {
    setRecoDeciding(decisionId);
    setRecoError(null);
    try {
      await api.post(
        `/ingest/document/reconciliation-queue/${decisionId}`,
        { decision }
      );
      // Remove resolved item from local list
      setRecoQueue(prev => prev.filter(item => item.decision_id !== decisionId));
      setRecoTotal(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      const msg = detail || (status ? `HTTP ${status}` : err?.message) || 'Failed to submit decision. Please try again.';
      setRecoError(msg);
    } finally {
      setRecoDeciding(null);
    }
  };

  // v2.84 - Listen for OAuth popup completion message - 2026-02-05
  // Accept messages from both mylojic.com and www.mylojic.com (origin mismatch fix)
  useEffect(() => {
    const handleOAuthMessage = (event: MessageEvent) => {
      // v2.84 - Accept both www and non-www origins (backend redirects to www)
      const validOrigins = [window.location.origin, 'https://mylojic.com', 'https://www.mylojic.com'];
      if (!validOrigins.includes(event.origin)) return;
      if (event.data?.type === 'oauth_success') {
        console.log('v2.84: OAuth popup success (DataSources):', event.data.connector, event.data.connectionId);
        setTimeout(() => {
          fetchData(); // Refresh connections list
          triggerGoldRefresh(); // v4.08 - UNIFY-01: gold-only refresh in background
        }, 1000);
      } else if (event.data?.type === 'oauth_error') {
        // v4.19 - AIRBYTE-BRIDGE-PLATFORM-CREDENTIALS S3 (F04): surface the failure.
        console.log('v4.19: OAuth popup error (DataSources):', event.data.error);
        // v4.32 - AIRBYTE-CONNECTOR-ROLLOUT-01 (F3): the plan-limit refusal is named to the customer.
        setError(event.data.error === PLAN_LIMIT_ERROR ? PLAN_LIMIT_MESSAGE
          : "We couldn't finish connecting your account. Please try again.");
      }
    };
    window.addEventListener('message', handleOAuthMessage);
    return () => window.removeEventListener('message', handleOAuthMessage);
  }, [fetchData, triggerGoldRefresh]);

  // v2.41 - Handle manual sync trigger - 2026-02-02
  const handleSync = async (connectionId: string) => {
    if (!tenantId || syncingConnectionId) return;

    setSyncingConnectionId(connectionId);
    setSyncNotice(null);
    // Update local state to show syncing
    // v4.21 - CONNECTOR-HEALTH-STATE-H2 (PD-13): also the verdict, so polling and the disabled refresh engage now
    setConnections(prev => prev.map(c =>
      c.id === connectionId ? { ...c, syncStatus: 'syncing', healthState: 'syncing' as HealthState, healthReason: null } : c
    ));

    try {
      const result = await connectionsService.sync(connectionId, tenantId);
      console.log('Sync result:', result);
      // Refresh data to get updated status
      await fetchData();
    } catch (err: any) {
      console.error('Sync failed:', err);
      // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-3d, H2-7c): a browser timeout means the sync is still running
      // (info; polling picks up the outcome); any other failure shows the H2-3d message, not the generic HTTP text.
      const outcome = manualSyncFailure(err);
      if (outcome.severity === 'info') setSyncNotice(outcome.message);
      else setError(outcome.message);
      // Refresh to get actual status (quiet: keeps the message)
      await refreshConnections();
    } finally {
      setSyncingConnectionId(null);
    }
  };

  // v3.00 - Handle report issue for a connector
  const handleReportIssue = async (connectionId: string) => {
    try {
      setReportingIssue(connectionId);
      const result = await connectorHealthService.reportIssue(connectionId);
      if (result.success) {
        setError(null);
        // Refresh data to show escalation flag
        await fetchData();
      }
    } catch (err: any) {
      console.error('v3.00: Failed to report issue:', err);
      setError(err?.userMessage || 'Failed to report issue');
    } finally {
      setReportingIssue(null);
    }
  };

  // Get status chip for a connection
  // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-3a; F16, F17): one switch on the server verdict through the shared helper.
  // `healthy` is the only green chip; needs_attention shows healthReason in its tooltip; unknown and any value
  // outside the union are neutral grey. lastSyncResult.errors is no longer read (v2.40 / v2.43 logic replaced).
  const healthIcon = (state: HealthState): React.ReactElement => {
    switch (state) {
      case 'healthy': return <CheckCircle />;
      case 'syncing': return <Sync className="spin" />;
      case 'setting_up': return <Schedule />;
      case 'needs_attention': return <ErrorIcon />;
      case 'disconnected': return <CloudOff />;
      default: return <HelpOutline />;
    }
  };
  const getStatusChip = (connection: Connection) => {
    const d = healthDisplay(connection.healthState, connection.healthReason);
    const chip = <Chip icon={healthIcon(d.state)} label={d.label} color={d.color} size="small" />;
    return d.reason ? <Tooltip title={d.reason} arrow>{chip}</Tooltip> : chip;
  };

  // Format date
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffHours < 48) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Get connector info for a connection
  const getConnectorForConnection = (connection: Connection): ConnectorInfo | undefined => {
    const connectorType = connection.sourceType?.toLowerCase();
    const allConnectors = [
      ...connectors.matched,
      ...connectors.recommended,
      ...Object.values(connectors.other).flat(),
    ];
    return allConnectors.find(c => c.connectorType.toLowerCase() === connectorType);
  };

  // Handle connect click
  const handleConnect = async (connector: ConnectorInfo) => {
    if (!tenantId) return;

    // v4.32 - AIRBYTE-CONNECTOR-ROLLOUT-01 (F1): the server's projected type, no default (was || 'oauth2').
    const authType = connector.authType?.toLowerCase();

    if (authType === 'api_key' || authType === 'basic') {
      // Show form modal
      try {
        const formResponse = await authService.getFormSchema(connector.connectorType);
        if (formResponse.flow_type === 'form' && formResponse.form_schema) {
          setCredentialFormSchema(formResponse.form_schema);
          setCredentialConnector(connector);
          setCredentialModalOpen(true);
        } else {
          setError(FORM_UNAVAILABLE_MESSAGE);  // v4.32 - ACR-01 (F1): was a silent no-op
        }
      } catch (err: any) {
        setError(err.userMessage || 'Failed to load connection form');
      }
    } else if (authType !== 'oauth2') {
      // v4.32 - ACR-01 (F1): absent or unknown sign-in type — never open a provider window.
      setError(FORM_UNAVAILABLE_MESSAGE);
    } else if (connector.preAuthParams && connector.preAuthParams.length > 0) {
      // v3.95 - Pre-auth params required: show modal before OAuth - 2026-04-06
      setPreAuthConnector(connector);
      setPreAuthValues({});
      setPreAuthErrors({});
      setPreAuthModalOpen(true);
      setConnectDialogOpen(false);
      return;
    } else {
      // v2.80 - OAuth opens in popup window (keeps browser history clean) - 2026-02-05
      connectionsService.startOAuthPopup(connector.connectorType, tenantId);
    }
    setConnectDialogOpen(false);
  };

  // Handle credential form submit
  const handleCredentialSubmit = async (data: Record<string, string>) => {
    if (!credentialConnector || !tenantId) return;

    setCredentialLoading(true);
    setCredentialError(null);

    try {
      const result = await authService.submitCredentials(credentialConnector.connectorType, {
        ...data,
        tenant_id: tenantId,
      });

      if (result.success) {
        setCredentialModalOpen(false);
        setCredentialFormSchema(null);
        setCredentialConnector(null);
        fetchData(); // Refresh list
        triggerGoldRefresh(); // v4.08 - UNIFY-01: gold-only refresh in background
      } else {
        setCredentialError(result.error || 'Failed to save credentials');
      }
    } catch (err: any) {
      setCredentialError(err.userMessage || 'Failed to save credentials');
    } finally {
      setCredentialLoading(false);
    }
  };

  // Handle delegation
  const handleDelegateClick = (connector: ConnectorInfo) => {
    setDelegationConnector(connector);
    setDelegationModalOpen(true);
    setConnectDialogOpen(false);
  };

  const handleSendDelegation = async (email: string, message?: string) => {
    if (!delegationConnector) return { success: false, error: 'No connector selected' };

    try {
      return await authService.createDelegation({
        connectorType: delegationConnector.connectorType,
        delegateEmail: email,
        message,
      });
    } catch (err: any) {
      return { success: false, error: err.userMessage || 'Failed to send request' };
    }
  };

  // v3.95 - Pre-auth modal: normalize + validate + launch OAuth popup - 2026-04-06
  const normalizePreAuthValue = (value: string, param: PreAuthParam): string => {
    let v = value.trim();
    v = v.replace(/^https?:\/\//, '');
    if (param.suffix) {
      const escaped = param.suffix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      v = v.replace(new RegExp(escaped + '.*$'), '');
    }
    v = v.split('/')[0];
    v = v.split('?')[0];
    return v;
  };

  const handlePreAuthSubmit = () => {
    if (!preAuthConnector?.preAuthParams || !tenantId) return;
    const errors: Record<string, string> = {};
    const normalized: Record<string, string> = {};
    for (const param of preAuthConnector.preAuthParams) {
      const val = normalizePreAuthValue(preAuthValues[param.key] || '', param);
      normalized[param.key] = val;
      if (param.required && !val) errors[param.key] = `${param.label} is required`;
      else if (param.pattern && val && !new RegExp(param.pattern).test(val))
        errors[param.key] = param.patternError || `Invalid ${param.label}`;
    }
    if (Object.keys(errors).length > 0) { setPreAuthErrors(errors); return; }
    setPreAuthModalOpen(false);
    connectionsService.startOAuthPopup(preAuthConnector.connectorType, tenantId, normalized);
    setPreAuthConnector(null);
  };

  // Handle disconnect
  const handleDisconnect = async () => {
    if (!selectedConnection || !tenantId) return;

    setDisconnecting(true);
    try {
      await connectionsService.delete(selectedConnection.id, tenantId); // v3.95 - PF-03: 2026-04-04
      setDisconnectDialogOpen(false);
      setSelectedConnection(null);
      await fetchData();
      // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-3c, PD-2): after a reconnect-disconnect the Connect dialog reopens;
      // the type is selectable again and the normal connect flow starts on the customer's own click (an OAuth
      // popup opened here, after an await, would be outside the click's user gesture and blocked).
      if (reconnectConnector) {
        setReconnectConnector(null);
        setConnectDialogOpen(true);
      }
    } catch (err: any) {
      setError(err.userMessage || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };

  // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-3c): a type whose connection needs attention or is disconnected opens
  // the existing Disconnect confirmation (reconnect wording) for that connection; no connect call is made here.
  const handleReconnectSelect = (connector: ConnectorInfo, connectionId: string) => {
    const existing = connections.find(c => c.id === connectionId) || null;
    setConnectDialogOpen(false);
    setSelectedConnection(existing);
    setReconnectConnector(connector);
    setDisconnectDialogOpen(true);
  };

  const closeDisconnectDialog = () => {
    setDisconnectDialogOpen(false);
    setReconnectConnector(null);
  };

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, connection: Connection) => {
    setMenuAnchor(event.currentTarget);
    setMenuConnection(connection);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuConnection(null);
  };

  // All available connectors for the dialog
  const allConnectors = [
    ...connectors.matched,
    ...connectors.recommended,
    ...Object.values(connectors.other).flat(),
  ].filter((c, i, arr) => arr.findIndex(x => x.connectorType === c.connectorType) === i);

  return (
    // v3.01 - Mobile responsive padding - 2026-03-20
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <TopBar
        title="Data Sources"
        subtitle={`${connections.length} connected source${connections.length !== 1 ? 's' : ''}`}
        actionButton={canManageConnections ? {
          label: 'Connect New Source',
          onClick: () => setConnectDialogOpen(true)
        } : undefined}
      />

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {error && loadState !== 'error' && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-7c): a manual sync that outlived the browser timeout */}
        {syncNotice && (
          <Alert severity="info" sx={{ mb: 3 }} onClose={() => setSyncNotice(null)}>
            {syncNotice}
          </Alert>
        )}

        {/* v4.08 - UNIFY-01: post-connect gold refresh banner */}
        {refreshBanner && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Data source connected. Refreshing your metrics…
          </Alert>
        )}

        {loading ? (
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map(i => (
              <Grid item xs={12} md={6} key={i}>
                <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        ) : loadState === 'error' ? (
          // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-7b): a failed first load is not an empty account
          <Card sx={{ textAlign: 'center', py: 6 }}>
            <CardContent>
              <ErrorIcon sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
              <Typography sx={{ color: 'text.primary', mb: 3 }}>
                {error || 'Failed to load data sources'}
              </Typography>
              <Button variant="contained" startIcon={<Refresh />} onClick={() => { fetchData(); }}>
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : connections.length === 0 ? (
          // Empty state
          <Card sx={{ textAlign: 'center', py: 8, bgcolor: '#f9f9f9' }}>
            <CardContent>
              <Storage sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                No Data Sources Connected
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 4, maxWidth: 400, mx: 'auto' }}>
                Connect your business tools to start seeing insights and analytics in your dashboard.
              </Typography>
              {canManageConnections && (
              <Button
                variant="contained"
                size="large"
                startIcon={<Add />}
                onClick={() => setConnectDialogOpen(true)}
                sx={{ bgcolor: '#008080', '&:hover': { bgcolor: '#006666' } }}
              >
                Connect Your First Source
              </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          // Connected sources list
          <Grid container spacing={3}>
            {connections.map((connection) => {
              const connector = getConnectorForConnection(connection);
              return (
                <Grid item xs={12} md={6} key={connection.id}>
                  <Card sx={{ position: 'relative', '&:hover': { boxShadow: 2 } }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ mr: 2, color: 'primary.main', mt: 0.5 }}>
                          {connector ? getIcon(connector.icon, 40) : <BusinessIcon sx={{ fontSize: 40 }} />}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                            {connector?.displayName || connection.sourceType}
                          </Typography>
                          {/* v2.42 - Use company name from tenant_meta, not connection */}
                          {companyName && (
                            <Typography variant="body2" color="text.secondary">
                              {companyName}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStatusChip(connection)}
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, connection)}
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Schedule sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Last synced: {formatDate(connection.lastSyncAt)}
                          </Typography>
                        </Box>
                        {/* v2.41 - Sync button triggers actual data sync - 2026-02-02 */}
                        {canManageConnections && (
                        <Tooltip title="Sync data now">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleSync(connection.id)}
                              disabled={isRefreshDisabled(connection, syncingConnectionId)} // v4.21 - H2-7d: keyed on healthState
                            >
                              <Refresh
                                fontSize="small"
                                className={syncingConnectionId === connection.id ? 'spin' : ''}
                              />
                            </IconButton>
                          </span>
                        </Tooltip>
                        )}
                      </Box>

                      {/* v4.21 - CONNECTOR-HEALTH-STATE-H2: progress keyed on the verdict */}
                      {(normalizeHealthState(connection.healthState) === 'syncing' || syncingConnectionId === connection.id) && (
                        <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />
                      )}

                      {/* v4.22 - CONNECTOR-HEALTH-STATE-H3 (H3-9; F85): Report Issue shows when this connection's own
                          healthState (H2's server verdict) is needs_attention — the retired per-type summary never fired */}
                      {(() => {
                        const isUnhealthy = normalizeHealthState(connection.healthState) === 'needs_attention';
                        const hasEscalation = connection.escalationStatus === 'open'; // v4.21 - H2-2 projects escalationStatus (F18)
                        if (!isUnhealthy && !hasEscalation) return null;
                        return (
                          <Box sx={{ mt: 1.5 }}>
                            {hasEscalation ? (
                              <Chip
                                icon={<CheckCircle />}
                                label="Issue Reported"
                                size="small"
                                variant="outlined"
                                color="default"
                                sx={{ opacity: 0.7 }}
                              />
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                color="warning"
                                startIcon={reportingIssue === connection.id ? <Sync className="spin" /> : <ReportProblemIcon />}
                                onClick={() => handleReportIssue(connection.id)}
                                disabled={reportingIssue === connection.id}
                              >
                                {reportingIssue === connection.id ? 'Reporting...' : 'Report Issue'}
                              </Button>
                            )}
                          </Box>
                        );
                      })()}

                      {/* v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-4, F84): the v3.66 AIR-04 SyncStatusChip is removed */}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}

            {/* Add new source card */}
            {canManageConnections && (
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  minHeight: 180,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed',
                  borderColor: 'divider',
                  bgcolor: 'transparent',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  },
                }}
                onClick={() => setConnectDialogOpen(true)}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Add sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    Connect Another Source
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            )}

          </Grid>
        )}
      </Container>

      {/* v3.99 - UNSTRUCTURED-01: Document upload section below connector cards */}
      {canUploadDocuments && (
      <Container maxWidth="lg">
        <DocumentUploadSection />
      </Container>
      )}

      {/* v4.01 - UNSTRUCTURED-01 Phase 3: Reconciliation queue — only renders when items exist */}
      {canUploadDocuments && (recoLoading || recoTotal > 0) && (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <Gavel sx={{ color: 'warning.main' }} />
            <Typography variant="h6" fontWeight={600}>
              Document Conflicts Needing Review
            </Typography>
            {recoTotal > 0 && (
              <Chip label={recoTotal} color="warning" size="small" />
            )}
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            These uploaded documents may duplicate records already imported from your connected
            sources. Review each conflict and confirm whether the document is the <strong>same
            record</strong> (suppress it) or a <strong>different transaction</strong> (keep it).
          </Alert>

          {recoError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRecoError(null)}>
              {recoError}
            </Alert>
          )}

          {recoLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <Typography color="text.secondary">Loading conflicts…</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {recoQueue.map((item: any) => {
                const srcA = item.source_a_ref || {};
                const srcB = item.source_b_ref || {};
                const signals = item.match_signals || {};
                const isDeciding = recoDeciding === item.decision_id;
                return (
                  <Card key={item.decision_id} variant="outlined" sx={{ p: 0 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 260 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Connector record ({srcA.connector_type || 'connector'})
                          </Typography>
                          <Typography variant="body2">
                            <strong>Amount:</strong> {signals.amount != null ? `$${Number(signals.amount).toFixed(2)}` : '—'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Date:</strong> {signals.date || '—'}
                          </Typography>
                          {srcA.description && (
                            <Typography variant="body2">
                              <strong>Description:</strong> {srcA.description}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                          <HelpOutline sx={{ fontSize: 28, color: 'warning.main' }} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 260 }}>
                          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Uploaded document ({srcB.filename || 'document'})
                          </Typography>
                          <Typography variant="body2">
                            <strong>Amount:</strong> {signals.doc_amount != null ? `$${Number(signals.doc_amount).toFixed(2)}` : '—'}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Date:</strong> {signals.doc_date || '—'}
                          </Typography>
                          {srcB.vendor_customer && (
                            <Typography variant="body2">
                              <strong>Vendor:</strong> {srcB.vendor_customer}
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          color="success"
                          size="small"
                          startIcon={<CheckCircle />}
                          disabled={isDeciding}
                          onClick={() => handleRecoDecide(item.decision_id, 'same')}
                        >
                          {isDeciding ? 'Saving…' : 'Same record — suppress document'}
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<Storage />}
                          disabled={isDeciding}
                          onClick={() => handleRecoDecide(item.decision_id, 'different')}
                        >
                          Different transaction — keep document
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </Container>
      )}

      {/* Connection menu */}
      {/* v2.41 - Updated menu to trigger actual sync - 2026-02-02 */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {canManageConnections && (
        <MenuItem
          onClick={() => {
            if (menuConnection) handleSync(menuConnection.id);
            handleMenuClose();
          }}
          disabled={!menuConnection || isRefreshDisabled(menuConnection, syncingConnectionId)}
        >
          <Sync sx={{ mr: 1 }} /> Sync Data Now
        </MenuItem>
        )}
        <MenuItem onClick={() => { fetchData(); handleMenuClose(); }}>
          <Refresh sx={{ mr: 1 }} /> Refresh Status
        </MenuItem>
        {canManageConnections && (
        <MenuItem onClick={() => {
          setSelectedConnection(menuConnection);
          setDisconnectDialogOpen(true);
          handleMenuClose();
        }}>
          <Delete sx={{ mr: 1, color: 'error.main' }} />
          <Typography color="error">Disconnect</Typography>
        </MenuItem>
        )}
      </Menu>

      {/* Connect New Source Dialog */}
      <Dialog
        open={connectDialogOpen}
        onClose={() => setConnectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Connect a Data Source</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 3, color: 'text.secondary' }}>
            Select a platform to connect. You can also delegate the connection to your accountant.
          </Typography>

          {allConnectors.map((connector) => {
            // v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-3c; F16): a listed connection no longer disables the type by
            // itself — needs_attention / disconnected types are re-connectable; healthy / syncing / setting_up /
            // unknown stay disabled as before.
            const action = dialogActionFor(connector.connectorType, connections);
            const existing = connections.find(
              c => c.sourceType?.toLowerCase() === connector.connectorType.toLowerCase()
            );
            const existingDisplay = existing ? healthDisplay(existing.healthState, existing.healthReason) : null;
            const isDisabled = action.kind === 'disabled' ||
              connector.connectorStatus === 'coming_soon' ||
              connector.connectorStatus === 'maintenance';
            const onSelect = () => {
              if (isDisabled) return;
              if (action.kind === 'reconnect') handleReconnectSelect(connector, action.connectionId);
              else handleConnect(connector);
            };

            return (
              <Card
                key={connector.connectorType}
                sx={{
                  mb: 2,
                  cursor: isDisabled ? 'default' : 'pointer',
                  opacity: isDisabled ? 0.6 : 1,
                  '&:hover': isDisabled ? {} : { boxShadow: 2 },
                }}
                onClick={onSelect}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', py: 2 }}>
                  <Box sx={{ mr: 2, color: isDisabled ? 'text.disabled' : 'primary.main' }}>
                    {getIcon(connector.icon, 36)}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {connector.displayName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {connector.description}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {existingDisplay && (
                      <Chip label={existingDisplay.label} size="small" color={existingDisplay.color} />
                    )}
                    {connector.connectorStatus === 'coming_soon' && (
                      <Chip label="Coming Soon" size="small" />
                    )}
                    {!isDisabled && canDelegateConnections && (
                      <Tooltip title="Delegate to accountant">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelegateClick(connector);
                          }}
                        >
                          <PersonAdd fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Disconnect Confirmation Dialog */}
      {/* v4.21 - CONNECTOR-HEALTH-STATE-H2 (H2-3c, PD-2): reconnect wording when opened from the Connect dialog */}
      <Dialog open={disconnectDialogOpen} onClose={closeDisconnectDialog}>
        <DialogTitle>
          {reconnectConnector ? `Reconnect ${reconnectConnector.displayName || selectedConnection?.sourceType || ''}?` : 'Disconnect Data Source?'}
        </DialogTitle>
        <DialogContent>
          {reconnectConnector ? (
            <Typography>
              To reconnect, the current connection is removed first. Then choose this source again to connect it.
            </Typography>
          ) : (
            <Typography>
              Are you sure you want to disconnect{' '}
              <strong>{selectedConnection?.sourceType}</strong>?
              This will stop syncing data from this source.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDisconnectDialog}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDisconnect}
            disabled={disconnecting}
          >
            {disconnecting ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credential Form Modal */}
      <CredentialFormModal
        open={credentialModalOpen}
        onClose={() => {
          setCredentialModalOpen(false);
          setCredentialFormSchema(null);
          setCredentialConnector(null);
          setCredentialError(null);
        }}
        formSchema={credentialFormSchema}
        connectorDisplayName={credentialConnector?.displayName || ''}
        onSubmit={handleCredentialSubmit}
        onDelegateClick={() => {
          if (credentialConnector) {
            setCredentialModalOpen(false);
            handleDelegateClick(credentialConnector);
          }
        }}
        loading={credentialLoading}
        error={credentialError}
      />

      {/* Delegation Modal */}
      <DelegationModal
        open={delegationModalOpen}
        onClose={() => {
          setDelegationModalOpen(false);
          setDelegationConnector(null);
        }}
        connectorType={delegationConnector?.connectorType || ''}
        connectorDisplayName={delegationConnector?.displayName || ''}
        onSendRequest={handleSendDelegation}
      />

      {/* v3.95 - Pre-Auth Modal: collect required params before OAuth start - 2026-04-06 */}
      <Dialog
        open={preAuthModalOpen}
        onClose={() => { setPreAuthModalOpen(false); setPreAuthConnector(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Connect {preAuthConnector?.displayName || ''}</DialogTitle>
        <DialogContent>
          {preAuthConnector?.preAuthParams?.map((param) => (
            <TextField
              key={param.key}
              label={param.label}
              placeholder={param.placeholder}
              value={preAuthValues[param.key] || ''}
              onChange={(e) => {
                setPreAuthValues(prev => ({ ...prev, [param.key]: e.target.value }));
                setPreAuthErrors(prev => { const n = { ...prev }; delete n[param.key]; return n; });
              }}
              error={!!preAuthErrors[param.key]}
              helperText={preAuthErrors[param.key]}
              fullWidth
              margin="normal"
              autoFocus
              InputProps={param.suffix ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography variant="body2" color="text.secondary">{param.suffix}</Typography>
                  </InputAdornment>
                ),
              } : undefined}
              onKeyDown={(e) => { if (e.key === 'Enter') handlePreAuthSubmit(); }}
            />
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setPreAuthModalOpen(false); setPreAuthConnector(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handlePreAuthSubmit}>Connect</Button>
        </DialogActions>
      </Dialog>

      {/* CSS for spinning sync icon */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </Box>
  );
};

export default DataSources;
